import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster";

/**
 * vidalv.live regional map – Leaflet implementation.
 * API kept identical to the previous MapLibre version so consumers don't need to change.
 *
 * Props:
 *  - variant: "hero" | "page"  (page is fully interactive, hero is decorative)
 *  - center: [lng, lat]        (kept lng-first for backward compat; we flip internally)
 *  - zoom: number
 *  - markers: [{id, lat, lng, cat?, number?, isUser?, popupHtml?, hoverLabel?, onClick?}]
 *  - lines:   [{name?, coords: [[lng,lat],…], color?}]
 *  - fitBounds: [[lng,lat],[lng,lat]] | null
 *  - gpxTracks: [{name?, geojson?, url?, color?}]  (optional, drawn as polyline)
 *  - tileLayer: "standard" | "topo"  (default "standard", "topo" = Lantmäteriet)
 *  - showLayerSwitcher: bool  (default true on variant="page")
 *  - className
 */
const CAT_COLORS = {
  Service: "#8E2816",
  Företag: "#0C4F8F",
  Förening: "#6b4a22",
  Friluftsliv: "#2A4B3C",
  Friluftsområde: "#1f3d2e",
  Sevärdhet: "#8E2816",
  Natur: "#2A4B3C",
  Lantbruk: "#8b6a1f",
  Kulturminne: "#6b2a6b",
  Evenemang: "#6b2a6b",
  Vardag: "#8b6a1f",
  // Nya kategorier (Build 86): Hjärtstartare, Återvinning, Badplats, Vindskydd, Grillplats, Båtramp
  Hjärtstartare: "#c22a1f",
  Återvinning: "#5d7a48",
  Badplats: "#2a6f9a",
  Vindskydd: "#5d4a2c",
  Grillplats: "#a04826",
  Båtramp: "#1f5a73",
  // Build 87 — generiskt platsformulär: offentliga toaletter, rast/lek/utsikt
  "Offentlig toalett": "#5d6b7a",
  Rastplats: "#8b6a1f",
  Lekplats: "#d49a2a",
  Utsiktsplats: "#3a5a7a",
};

const STANDARD_TILE = {
  url: "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
  attribution: '<a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">© OpenStreetMap-bidragsgivare</a>',
  maxZoom: 19,
};

// Lantmäteriet open WMTS topographic basemap (publik, ingen nyckel krävs).
// Källa: https://api.lantmateriet.se/open/topowebb-ccby/v1/wmts/token/.../
const TOPO_TILE = {
  url: "https://opencache.statkart.no/gatekeeper/gk/gk.open_gmaps?layers=topo4&zoom={z}&x={x}&y={y}",
  attribution: '© <a href="https://www.kartverket.no" target="_blank" rel="noopener noreferrer">Kartverket</a> · OSM-fallback',
  maxZoom: 18,
};

export default function RegionMap({
  variant = "hero",
  center = [16.362, 63.878],
  zoom = 9.2,
  markers = [],
  lines = [],
  fitBounds = null,
  gpxTracks = [],
  tileLayer = "standard",
  showLayerSwitcher,
  className = "",
}) {
  const container = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (!container.current || mapRef.current) return;

    const interactive = variant === "page";
    const containerEl = container.current;
    const switcher = showLayerSwitcher ?? interactive;

    // Leaflet uses [lat, lng] but our existing data is [lng, lat] – we keep the prop shape
    // (compatible with previous MapLibre version) and flip internally.
    const [lng, lat] = center;
    const map = L.map(containerEl, {
      center: [lat, lng],
      zoom: Math.max(Math.round(zoom), 4),
      minZoom: variant === "hero" ? Math.max(Math.round(zoom) - 1, 4) : 6,
      maxZoom: 18,
      zoomControl: interactive,
      dragging: interactive,
      scrollWheelZoom: interactive,
      doubleClickZoom: interactive,
      boxZoom: interactive,
      keyboard: interactive,
      touchZoom: interactive,
      attributionControl: true,
      // Build 94b — popups stänger sig inte automatiskt vid karttap. Mobile-skydd.
      closePopupOnClick: false,
    });
    mapRef.current = map;

    const standard = L.tileLayer(STANDARD_TILE.url, {
      attribution: STANDARD_TILE.attribution,
      maxZoom: STANDARD_TILE.maxZoom,
    });
    const topo = L.tileLayer(TOPO_TILE.url, {
      attribution: TOPO_TILE.attribution,
      maxZoom: TOPO_TILE.maxZoom,
    });
    const baseLayers = { Standardkarta: standard, "Topografisk karta": topo };
    (tileLayer === "topo" ? topo : standard).addTo(map);

    if (switcher) {
      L.control.layers(baseLayers, undefined, { position: "topright", collapsed: true }).addTo(map);
    }

    // Resize observer (Leaflet auto-handles, but we trigger invalidateSize on container changes)
    const ro = new ResizeObserver(() => {
      try { map.invalidateSize(); } catch { /* race med unmount */ }
    });
    ro.observe(containerEl);

    // Lines from props (existing trail polylines, kept for backward compat)
    lines.forEach((l) => {
      if (!l.coords || l.coords.length < 2) return;
      const latlngs = l.coords.map(([x, y]) => [y, x]); // lng,lat → lat,lng
      // Halo
      L.polyline(latlngs, { color: "#f4ead4", weight: 7, opacity: 0.6, lineCap: "round", lineJoin: "round" }).addTo(map);
      // Main
      L.polyline(latlngs, {
        color: l.color || "#3F6B4E",
        weight: 3,
        opacity: 0.9,
        dashArray: "6 6",
        lineCap: "round",
        lineJoin: "round",
      }).addTo(map);
    });

    // GPX tracks (optional, solid lines, no dash)
    gpxTracks.forEach((t) => {
      const geo = t.geojson;
      if (!geo) return;
      L.geoJSON(geo, {
        style: { color: t.color || "#B05B41", weight: 4, opacity: 0.9, lineCap: "round", lineJoin: "round" },
      }).addTo(map);
    });

    if (fitBounds && fitBounds.length === 2) {
      const [[w, s], [e, n]] = fitBounds; // lng,lat pairs
      map.fitBounds([[s, w], [n, e]], { padding: [40, 40], maxZoom: 14 });
    }

    // Marker cluster only for many markers on interactive map
    const useCluster = interactive && markers.length > 25;
    const cluster = useCluster
      ? L.markerClusterGroup({
          showCoverageOnHover: false,
          spiderfyOnMaxZoom: true,
          maxClusterRadius: 45,
        })
      : null;

    const updateCompactClass = () => {
      const isCompact = map.getZoom() < 11;
      containerEl.querySelectorAll(".vidalv-num-marker").forEach((el) => {
        el.classList.toggle("is-compact", isCompact);
      });
    };
    map.on("zoom", updateCompactClass);

    markers.forEach((m) => {
      const color = CAT_COLORS[m.cat] || CAT_COLORS.Sevärdhet;
      const el = document.createElement("div");
      el.setAttribute("data-testid", `map-marker-${m.id}`);

      if (m.isUser) {
        el.className = "vidalv-user-marker";
        el.innerHTML = '<span class="vidalv-user-marker-core"></span><span class="vidalv-user-marker-pulse"></span>';
      } else if (m.number != null) {
        el.className = "vidalv-num-marker";
        el.style.setProperty("--num-color", color);
        el.textContent = String(m.number);
      } else {
        el.className = "vidalv-marker-pin";
        el.innerHTML = `<span class="vidalv-marker-pin-dot" style="background-color:${color}"></span>`;
      }

      const icon = L.divIcon({
        html: el.outerHTML,
        className: "vidalv-leaflet-marker",
        // Build 96.9 — pill-form för text-nr (K#7, V#123). Ökad bredd för click-yta.
        iconSize: m.number != null ? [44, 28] : [22, 22],
        iconAnchor: m.number != null ? [22, 14] : [11, 11],
      });
      const marker = L.marker([m.lat, m.lng], { icon, keyboard: false, interactive: true });

      if (m.popupHtml) {
        marker.bindPopup(m.popupHtml, {
          offset: [0, -8],
          className: "vidalv-popup",
          closeButton: true,
        });
      }
      if (m.hoverLabel && !m.isUser) {
        marker.bindTooltip(m.hoverLabel, {
          direction: "top",
          offset: [0, -10],
          className: "vidalv-hover-tip",
          opacity: 0.98,
          sticky: false,
          permanent: false,
        });
        // Säkerställ att hovern fungerar även när Leaflets default-event någon gång
        // inte triggar (t.ex. när divIcon-innehållet sluker pekarhändelsen).
        marker.on("mouseover", function () { this.openTooltip(); });
        marker.on("mouseout", function () { this.closeTooltip(); });
      }
      if (m.onClick) {
        marker.on("click", (ev) => {
          if (ev.originalEvent) ev.originalEvent.stopPropagation();
          m.onClick(m);
        });
      }
      if (cluster) cluster.addLayer(marker);
      else marker.addTo(map);
    });
    if (cluster) cluster.addTo(map);

    setTimeout(updateCompactClass, 250);

    return () => {
      ro.disconnect();
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    variant,
    JSON.stringify(markers.map((m) => m.id)),
    JSON.stringify(lines.map((l) => l.name)),
    JSON.stringify(gpxTracks.map((t) => t.name || t.url || "")),
    tileLayer,
  ]);

  return (
    <div
      ref={container}
      data-testid={`region-map-${variant}`}
      className={className}
      style={{ width: "100%", height: "100%" }}
    />
  );
}
